import { useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export type DashboardCardState = 'loading' | 'error' | 'empty' | 'success'

export interface DashboardCardProps {
  title: string
  titleId?: string
  action?: ReactNode
  state: DashboardCardState
  children?: ReactNode
  emptyMessage?: string
  emptyAction?: ReactNode
  errorMessage?: string
  onRetry?: () => void
  /** Esqueleto con la forma final del contenido mientras carga. */
  skeleton?: ReactNode
  /** Deja visible un menú desplegable que sobresale de la tarjeta. */
  allowOverflow?: boolean
  /** Permite contraer o desplegar el contenido de la tarjeta. */
  collapsible?: boolean
  /** Estado inicial cuando `collapsible` está activo (por defecto `true`). */
  defaultExpanded?: boolean
  className?: string
}

/**
 * Tarjeta base del dashboard: título, acción y los cuatro estados.
 * Se apoya en `Card` para no repetir el bloque de tarjeta en cada sección.
 */
export function DashboardCard({
  title,
  titleId,
  action,
  state,
  children,
  emptyMessage = 'Todavía no hay datos para mostrar.',
  emptyAction,
  errorMessage = 'No pudimos cargar esta sección.',
  onRetry,
  skeleton,
  allowOverflow = false,
  collapsible = false,
  defaultExpanded = true,
  className,
}: DashboardCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const isExpanded = collapsible ? expanded : true
  const contentId = titleId ? `${titleId}-content` : undefined

  const handleToggle = () => {
    if (collapsible) {
      setExpanded((prev) => !prev)
    }
  }

  return (
    <Card
      className={cn(
        'rounded-2xl border-slate-100 p-0 shadow-sm',
        allowOverflow ? 'overflow-visible' : 'overflow-hidden',
        className
      )}
      aria-labelledby={titleId}
    >
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-3 px-4 py-3',
          isExpanded && 'border-b border-slate-100'
        )}
      >
        <h3
          id={titleId}
          onClick={collapsible ? handleToggle : undefined}
          className={cn(
            'text-sm font-bold text-slate-800',
            collapsible && 'cursor-pointer select-none'
          )}
        >
          {title}
        </h3>
        <div className="flex items-center gap-3">
          {action}
          {collapsible && (
            <button
              type="button"
              onClick={handleToggle}
              aria-expanded={isExpanded}
              aria-controls={contentId}
              aria-label={isExpanded ? `Contraer ${title}` : `Expandir ${title}`}
              title={isExpanded ? 'Contraer' : 'Expandir'}
              className="inline-flex items-center gap-1 rounded-lg p-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <ChevronDown
                className={cn(
                  'h-4 w-4 transition-transform duration-200',
                  isExpanded && 'rotate-180'
                )}
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div id={contentId}>
          {state === 'loading' && (
            <div aria-busy="true" aria-label={`Cargando ${title.toLowerCase()}`} className="space-y-3 p-4">
              {skeleton ?? (
                <>
                  <Skeleton height={16} className="w-2/3" />
                  <Skeleton height={16} className="w-1/2" />
                  <Skeleton height={16} className="w-3/4" />
                </>
              )}
            </div>
          )}

          {state === 'error' && (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 p-4">
              <p className="text-sm font-medium text-rose-800">{errorMessage}</p>
              {onRetry && (
                <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                  Reintentar
                </Button>
              )}
            </div>
          )}

          {state === 'empty' && (
            <div className="flex flex-col items-center gap-3 p-6 text-center">
              <p className="text-sm font-medium text-slate-700">{emptyMessage}</p>
              {emptyAction}
            </div>
          )}

          {state === 'success' && children}
        </div>
      )}
    </Card>
  )
}
